import React, { useState, useEffect } from "react";
import { Layout, Avatar, Badge, Button, Popover, List, Typography, Empty, Spin } from "antd";
import { BellOutlined } from "@ant-design/icons";
import { useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { getNotificationHistoryApi, markActivityReadApi } from "../services/api";

const { Header } = Layout;
const { Text } = Typography;

const TYPE_COLORS = {
  // Admin-fired broadcast campaigns (Notifications page compose form)
  OFFER:        "#f04d4d",
  NEW_ARRIVAL:  "#4a90d9",
  HEALTH_TIP:   "#52c41a",
  ANNOUNCEMENT: "#722ed1",
  REMINDER:     "#13c2c2",
  GENERAL:      "#6b7280",
  // Order-lifecycle pushes (OrdersService) — these carry orderId and are
  // what makes a row clickable; used to just show as generic "ADMIN"/"ORDER"
  // (the routing scope) before NotificationService started logging the real
  // event type.
  ORDER_PLACED:           "#534ab7",
  ORDER_NEEDS_ACCEPTANCE: "#f5a623",
  ORDER_VENDOR_REJECTED:  "#f04d4d",
  ORDER_STATUS_UPDATED:   "#1565C0",
  ORDER_CANCELLED:        "#dc2626",
};

// "ORDER_STATUS_UPDATED" -> "ORDER STATUS UPDATED" — readable in the badge
// instead of the raw underscored constant.
const formatType = (type) => (type || "").replace(/_/g, " ");

function NotificationPopoverContent({ onViewAll, onOrderClick }) {
  const [logs,    setLogs]    = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    getNotificationHistoryApi()
      .then((res) => setLogs(res.data?.data || []))
      .catch(() => setLogs([]))
      .finally(() => setLoading(false));
  }, []);

  if (loading) return (
    <div style={{ width: 320, display: "flex", justifyContent: "center", padding: 24 }}>
      <Spin />
    </div>
  );

  if (logs.length === 0) return (
    <div style={{ width: 320, padding: 16 }}>
      <Empty description="No notifications sent yet" imageStyle={{ height: 48 }} />
    </div>
  );

  return (
    <div style={{ width: 340, maxHeight: 400, overflowY: "auto" }}>
      <List
        dataSource={logs}
        renderItem={(item) => (
          <List.Item
            style={{
              padding: "10px 16px",
              alignItems: "flex-start",
              cursor: item.orderId ? "pointer" : "default",
            }}
            onClick={() => item.orderId && onOrderClick(item.orderId)}
          >
            <div style={{ width: "100%" }}>
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 2 }}>
                <Text strong style={{ fontSize: 13, flex: 1, marginRight: 8 }}>{item.title}</Text>
                <span style={{
                  fontSize: 10, fontWeight: 700, padding: "1px 7px", borderRadius: 10,
                  background: `${TYPE_COLORS[item.type] || "#6b7280"}18`,
                  color: TYPE_COLORS[item.type] || "#6b7280",
                  whiteSpace: "nowrap",
                }}>
                  {formatType(item.type)}
                </span>
              </div>
              <Text type="secondary" style={{ fontSize: 12, display: "block", marginBottom: 4 }}>
                {item.body}
              </Text>
              <Text type="secondary" style={{ fontSize: 11 }}>
                {item.deviceCount} device(s) · {new Date(item.sentAt).toLocaleString()}
              </Text>
            </div>
          </List.Item>
        )}
      />
      <div
        onClick={onViewAll}
        style={{
          textAlign: "center", padding: "10px 0",
          borderTop: "1px solid #f0f0f0", cursor: "pointer",
          color: "#4a90d9", fontWeight: 600, fontSize: 13,
        }}
      >
        View All / Send New
      </div>
    </div>
  );
}

export default function HeaderBar({ openProfile, onMenuClick, isMobile }) {
  const { user }  = useSelector((state) => state.auth);
  const navigate  = useNavigate();
  const [open, setOpen]     = useState(false);
  const [count, setCount]   = useState(0);

  useEffect(() => {
    getNotificationHistoryApi()
      .then((res) => setCount((res.data?.data || []).length))
      .catch(() => {});
  }, []);

  const handleViewAll = () => {
    setOpen(false);
    navigate("/notifications");
  };

  // Same deep-link Dashboard's Recent Activity uses (Orders.jsx reads
  // location.state.openOrderId and auto-opens that order's modal). Also marks
  // it read so the order drops off Recent Activity too — both surfaces point
  // at the same underlying order, so "seen it here" should count as "seen it".
  const handleOrderClick = (orderId) => {
    setOpen(false);
    markActivityReadApi(orderId).catch(() => {});
    navigate("/orders", { state: { openOrderId: orderId } });
  };

  return (
    <Header
      style={{
        background: "#fff",
        height: 64,
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        padding: "0 16px",
        borderBottom: "1px solid #eee",
      }}
    >
      {/* LEFT */}
      <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
        {isMobile && (
          <Button type="text" onClick={onMenuClick}>☰</Button>
        )}
        <div style={{ fontWeight: 500 }}>Dashboard</div>
      </div>

      {/* RIGHT */}
      <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
        <Popover
          open={open}
          onOpenChange={setOpen}
          trigger="click"
          placement="bottomRight"
          title={
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
              <span style={{ fontWeight: 700 }}>Sent Notifications</span>
              <Text type="secondary" style={{ fontSize: 12 }}>{count} total</Text>
            </div>
          }
          content={<NotificationPopoverContent onViewAll={handleViewAll} onOrderClick={handleOrderClick} />}
        >
          <Badge count={count} overflowCount={99}>
            <Button
              shape="circle"
              icon={<BellOutlined style={{ fontSize: 16 }} />}
            />
          </Badge>
        </Popover>

        <Avatar
          onClick={openProfile}
          style={{ cursor: "pointer", background: "#534ab7" }}
        >
          {user?.avatar || "U"}
        </Avatar>
      </div>
    </Header>
  );
}
